<template>
  <section class="customer-users" aria-label="Customer users">
    <div class="users-heading">
      <div><h3>Users</h3><p>Manage user accounts and access for this customer.</p></div>
      <b-button class="btn-green" @click="edit()">Add user</b-button>
    </div>
    <p v-if="loading && !loaded" role="status">Loading users…</p>
    <div v-else-if="error && !loaded" role="alert">
      <p>Could not load users.</p><b-button variant="outline-secondary" @click="fetchUsers">Try again</b-button>
    </div>
    <p v-else-if="!users.length">No users added for this customer.</p>
    <div v-else class="users-table-wrap">
      <table class="users-table">
        <thead><tr><th>Name</th><th>Email address</th><th>Role</th><th>Actions</th></tr></thead>
        <tbody>
          <tr v-for="user in users" :key="user.id || user.email">
            <td>{{ [user.firstName, user.lastName].filter(Boolean).join(' ') || '—' }}</td>
            <td>{{ user.email }}</td><td>{{ getRoleLabel(user.role) }}</td>
            <td class="users-actions">
              <b-button variant="outline-secondary" :aria-label="`Edit ${user.email}`" @click="edit(user)">Edit</b-button>
              <b-button v-if="account.isStaff" variant="outline-secondary" :aria-label="`Log in as ${user.email}`" @click="login(user)">Log in</b-button>
              <b-button variant="outline-danger" class="users-delete" :aria-label="`Delete ${user.email}`" @click="remove(user)">Delete</b-button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <p v-if="error && loaded" role="alert" class="text-danger">Could not refresh users. The previous list is still shown.</p>
    <p v-if="actionError" role="alert" class="text-danger">Could not complete the action. Try again.</p>
  </section>
</template>
<script setup lang="ts">
import type { Account } from '@/types/Models/Account'
import type { CustomerUsersProps } from '@/types/Models/Company'
import { BButton } from 'bootstrap-vue-next'
import { ref, watch } from 'vue'
import { USERS_CREATE_EDIT } from '@/const/ModalTypes'
import { getRoleLabel } from '@/services/Helpers'
import { openModal } from '@/services/Modal'
import { useAccountStore } from '@/store/account'
import { useAuthStore } from '@/store/auth'
import { useVueNextModalStore } from '@/store/vueNextModal'
import AccountProxy from '@/proxies/AccountProxy'
import AccountTransformer from '@/transformers/AccountTransformer'
const props = defineProps<CustomerUsersProps>()
const account = useAccountStore()
const auth = useAuthStore()
const modals = useVueNextModalStore()
const users = ref<Account[]>([])
const loading = ref(false)
const loaded = ref(false)
const error = ref(false)
const actionError = ref(false)
let request = 0
async function fetchUsers() {
  const current = ++request
  loading.value = true
  error.value = false
  try {
    const response = await new AccountProxy({ companyId: props.companyId }).all()
    if (current === request) {
      users.value = AccountTransformer.fetchCollection(response.data)
      loaded.value = true
    }
  } catch {
    if (current === request) error.value = true
  } finally {
    if (current === request) loading.value = false
  }
}
function edit(user?: Account) {
  openModal(USERS_CREATE_EDIT, { companyId: props.companyId, user })
}
async function login(user: Account) {
  if (!user.id) return
  actionError.value = false
  try { await auth.authAsCompany(props.companyId, user.id) } catch { actionError.value = true }
}
function remove(user: Account) {
  if (!user.id) return
  openModal('confirmWrap', {
    title: 'Delete user?',
    message: `The user account ${user.email} will be deleted.`,
    okButtonText: 'Delete',
    onConfirm: async () => {
      actionError.value = false
      try { await new AccountProxy().destroy(user.id!); await fetchUsers() } catch { actionError.value = true }
    },
  })
}
watch(() => props.companyId, () => {
  loaded.value = false
  users.value = []
  fetchUsers()
}, { immediate: true })
watch(() => modals.isModalVisible(USERS_CREATE_EDIT), (visible, previous) => {
  if (previous && !visible) fetchUsers()
})
</script>
<style scoped>
.users-heading { display: flex; justify-content: space-between; align-items: center; gap: 16px; margin-bottom: 20px; }
h3 { font-size: 18px; font-weight: 600; }
p { color: #59635e; margin-bottom: 8px; }
.users-table-wrap { overflow-x: auto; border: 1px solid #dfe5e2; border-radius: 6px; background: white; }
.users-table { width: 100%; border-collapse: collapse; }
.users-table th, .users-table td { text-align: left; padding: 16px; border-bottom: 1px solid #e6ebe8; }
.users-table th { background: #f6f8f7; font-weight: 600; }
.users-table tr:last-child td { border-bottom: 0; }
.users-actions { display: flex; gap: 8px; }
.users-actions .btn { white-space: nowrap; }
.users-actions .users-delete {
  color: #b42332;
  border-color: #b42332;
  background-color: transparent;
}
.users-actions .users-delete:hover,
.users-actions .users-delete:active {
  color: #fff;
  background-color: #b42332;
  border-color: #b42332;
}
.users-actions .users-delete:focus-visible {
  outline: 3px solid #b42332;
  outline-offset: 3px;
}
</style>
